"use client";

import React, { 
  createContext, 
  useContext, 
  useState, 
  useCallback, 
  useMemo,
  useEffect,
  ReactNode 
} from 'react';
import { 
  LeaveReviewFormData, 
  createInitialFormData
} from '@/lib/types/leave-review';

const STORAGE_KEY = 'leave_review_form_secure';

const getClientISOString = (): string => {
  return typeof window !== 'undefined' ? new Date().toISOString() : '';
};

const isSessionStorageAvailable = (): boolean => {
  if (typeof window === 'undefined') return false;
  
  try {
    const testKey = '__test_session_storage__';
    window.sessionStorage.setItem(testKey, 'test');
    window.sessionStorage.removeItem(testKey);
    return true;
  } catch {
    return false;
  }
};

const sessionStorage = {
  save: (data: LeaveReviewFormData): boolean => {
    if (!isSessionStorageAvailable()) return false;
    
    try {
      const payload = {
        version: '1.0',
        timestamp: Date.now(),
        data: data,
      };
      
      window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
      return true;
    } catch (error) {
      console.error('[LeaveReviewData] Failed to save form data:', error);
      return false;
    }
  },

  load: (): LeaveReviewFormData | null => {
    if (!isSessionStorageAvailable()) return null;
    
    try {
      const stored = window.sessionStorage.getItem(STORAGE_KEY);
      if (!stored) return null;
      
      const payload = JSON.parse(stored);
      
      // Support both old and new payload formats
      if (!payload.data || !payload.timestamp) return null;
      
      const currentTimestamp = Date.now();
      const isStale = currentTimestamp - payload.timestamp > 24 * 60 * 60 * 1000;
      if (isStale) {
        sessionStorage.clear();
        return null;
      }
      
      return payload.data;
    } catch (error) {
      console.error('[LeaveReviewData] Failed to load form data:', error);
      sessionStorage.clear();
      return null;
    }
  },

  clear: (): boolean => {
    if (!isSessionStorageAvailable()) return false;
    
    try {
      window.sessionStorage.removeItem(STORAGE_KEY);
      return true;
    } catch {
      return false;
    }
  }
};

interface LeaveReviewDataContextType {
  formData: LeaveReviewFormData;
  updateFormData: (updates: Partial<LeaveReviewFormData>) => void;
  updateNestedField: <T extends keyof LeaveReviewFormData>(
    section: T,
    field: keyof LeaveReviewFormData[T],
    value: LeaveReviewFormData[T][keyof LeaveReviewFormData[T]]
  ) => void;
  resetFormData: () => void;
  saveFormData: () => Promise<boolean>;
}

const LeaveReviewDataContext = createContext<LeaveReviewDataContextType>({
  formData: createInitialFormData(),
  updateFormData: () => {},
  updateNestedField: () => {},
  resetFormData: () => {},
  saveFormData: async () => false,
});

interface LeaveReviewDataProviderProps {
  children: ReactNode;
}

export function LeaveReviewDataProvider({ children }: LeaveReviewDataProviderProps) {
  const [formData, setFormData] = useState<LeaveReviewFormData>(() => {
    // Always start with initial data for immediate availability
    const initialData = createInitialFormData();
    
    // Try to load saved data asynchronously
    if (typeof window !== 'undefined') {
      try {
        const savedData = sessionStorage.load();
        return savedData || initialData;
      } catch (error) {
        console.warn('[LeaveReviewData] Failed to load initial data, using defaults:', error);
        return initialData;
      }
    }
    return initialData;
  });

  const updateFormData = useCallback((updates: Partial<LeaveReviewFormData>) => {
    setFormData(currentFormData => ({
      ...currentFormData,
      ...updates,
      lastUpdated: getClientISOString(),
    }));
  }, []);

  const updateNestedField = useCallback(<T extends keyof LeaveReviewFormData>(
    section: T,
    field: keyof LeaveReviewFormData[T],
    value: LeaveReviewFormData[T][keyof LeaveReviewFormData[T]]
  ) => {
    setFormData(currentFormData => {
      const sectionData = currentFormData[section];
      return {
        ...currentFormData,
        [section]: {
          ...(typeof sectionData === 'object' && sectionData !== null ? sectionData : {}),
          [field]: value,
        },
        lastUpdated: getClientISOString(),
      };
    });
  }, []);

  const resetFormData = useCallback(() => {
    const initialData = createInitialFormData();
    setFormData(initialData);
    sessionStorage.clear();
  }, []);

  const saveFormData = useCallback(async (): Promise<boolean> => {
    return sessionStorage.save(formData);
  }, [formData]);

  useEffect(() => {
    const autoSaveInterval = setInterval(() => {
      sessionStorage.save(formData);
    }, 30000);

    return () => clearInterval(autoSaveInterval);
  }, [formData]);

  const contextValue = useMemo(() => ({
    formData,
    updateFormData,
    updateNestedField,
    resetFormData,
    saveFormData,
  }), [formData, updateFormData, updateNestedField, resetFormData, saveFormData]);

  return (
    <LeaveReviewDataContext.Provider value={contextValue}>
      {children}
    </LeaveReviewDataContext.Provider>
  );
}

export function useLeaveReviewData(): LeaveReviewDataContextType {
  const context = useContext(LeaveReviewDataContext);
  if (!context) {
    // Return safe defaults when called outside provider (e.g., landing page)
    return {
      formData: createInitialFormData(),
      updateFormData: () => {},
      updateNestedField: () => {},
      resetFormData: () => {},
      saveFormData: async () => false,
    };
  }
  return context;
} 